/**
 * A caixa-preta do lado do aparelho: junta evento, e manda em lote.
 *
 * Regras que governam este arquivo, todas pela mesma razão — a telemetria NEVER
 * pode competir com o jogo que ela mede:
 *
 * 1. **NEVER aloca no laço de render.** Quem chama `record` no caminho quente
 *    passa um objeto já pronto; a fila só guarda a referência.
 * 2. **NEVER manda pelo WebSocket.** O socket tem orçamento MEDIDO (`ADR-0017`:
 *    ~2,1 KB/s em quatro assentos). Isto vai por HTTP, em outra conexão.
 * 3. **NEVER espera resposta.** `keepalive` no envio periódico e `sendBeacon` na
 *    saída: os dois entregam sem segurar a página.
 * 4. **NEVER quebra o jogo.** Toda falha aqui é engolida e vira, no máximo, um
 *    evento a menos.
 */
import type { TelemetryBatch, TelemetryEvent } from '@game/telemetry';
import { TELEMETRY_SCHEMA_VERSION } from '@game/telemetry';
import type { BuildInfo } from '../build-info.js';
import { TelemetryQueue } from './queue.js';
import type { TelemetrySession } from './session.js';

/** Intervalo de envio. Alto o bastante para virar UM pedido por partida curta. */
export const FLUSH_INTERVAL_MS = 5000;

export interface RecorderDeps {
  readonly session: TelemetrySession;
  readonly build: BuildInfo;
  /** Origem do coletor. Mesma que serviu a página, salvo configuração. */
  readonly endpoint: string;
  /** Injetáveis: o teste NEVER precisa de browser nem de rede. */
  readonly now?: () => number;
  readonly send?: (url: string, body: string, beacon: boolean) => boolean;
}

/**
 * Quem está gravando. Com a sessão sem código de teste, TODO método é inerte —
 * e o chamador NEVER ramifica, do mesmo jeito que o `disabledRecorder` do
 * servidor: `if (telemetria) …` espalhado é como um caminho acaba sem teste.
 */
export interface Recorder {
  readonly enabled: boolean;
  record(event: TelemetryEvent): void;
  /** Amarra os próximos eventos a uma partida. */
  setMatch(matchId: string | undefined, playerId: number | undefined): void;
  /** Milissegundos desde o começo da sessão. É o `t` de todo evento. */
  elapsedMs(): number;
  flush(beacon?: boolean): void;
  stop(): void;
}

const inertRecorder: Recorder = {
  enabled: false,
  record() {},
  setMatch() {},
  elapsedMs() {
    return 0;
  },
  flush() {},
  stop() {},
};

/**
 * `text/plain`, e o corpo é JSON assim mesmo. NEVER é descuido — é o que evita o
 * preflight.
 *
 * MEDIDO no browser: com `content-type: application/json` o pedido deixa de ser
 * "simples" para o CORS, e o browser manda um `OPTIONS` antes. Isso custa uma
 * viagem a mais no celular, e pior: `sendBeacon` sai com a página já indo embora,
 * quando NEVER há tempo para duas viagens — o último lote de toda sessão morreria
 * exatamente assim, em silêncio.
 *
 * `text/plain` está na lista de valores que o CORS considera seguros, então o
 * pedido vai direto. O coletor NEVER olha o cabeçalho: ele lê o corpo como texto
 * e faz `JSON.parse`.
 */
const CONTENT_TYPE = 'text/plain;charset=UTF-8';

function defaultSend(url: string, body: string, beacon: boolean): boolean {
  if (beacon && typeof navigator.sendBeacon === 'function') {
    // O ÚNICO caminho que sobrevive à aba fechando. `fetch` normal é cancelado
    // com a página, e é justamente o último lote — o do fim da partida — que
    // carrega o que aconteceu logo antes de o testador sair.
    return navigator.sendBeacon(url, new Blob([body], { type: CONTENT_TYPE }));
  }
  void fetch(url, {
    method: 'POST',
    body,
    // `keepalive` deixa o pedido sobreviver a uma navegação em curso.
    keepalive: true,
    headers: { 'content-type': CONTENT_TYPE },
    // NEVER manda cookie: o coletor NEVER tem sessão, e mandar credencial para
    // uma rota que NEVER a usa é superfície de graça.
    credentials: 'omit',
  }).catch(() => undefined);
  return true;
}

class ActiveRecorder implements Recorder {
  readonly enabled = true;

  private readonly queue = new TelemetryQueue();
  private readonly startedAt: number;
  private readonly now: () => number;
  private readonly send: (url: string, body: string, beacon: boolean) => boolean;
  private timer: ReturnType<typeof setInterval> | undefined;
  private matchId: string | null = null;
  private playerId: number | null = null;

  constructor(
    private readonly deps: RecorderDeps,
    private readonly test: string,
  ) {
    this.now = deps.now ?? (() => performance.now());
    this.send = deps.send ?? defaultSend;
    this.startedAt = this.now();
  }

  start(): void {
    this.timer = setInterval(() => {
      this.flush(false);
    }, FLUSH_INTERVAL_MS);
  }

  elapsedMs(): number {
    return Math.round(this.now() - this.startedAt);
  }

  record(event: TelemetryEvent): void {
    this.queue.push(event);
  }

  setMatch(matchId: string | undefined, playerId: number | undefined): void {
    this.matchId = matchId ?? null;
    this.playerId = playerId ?? null;
  }

  flush(beacon = false): void {
    const events = this.queue.drain(this.elapsedMs());
    if (events.length === 0) return;

    const batch: TelemetryBatch = {
      v: TELEMETRY_SCHEMA_VERSION,
      test: this.test,
      clientId: this.deps.session.clientId,
      tabId: this.deps.session.tabId,
      run: this.deps.session.run,
      build: { sha: this.deps.build.sha, time: this.deps.build.time, env: this.deps.build.env },
      matchId: this.matchId,
      playerId: this.playerId,
      events,
    };

    let ok: boolean;
    try {
      ok = this.send(this.deps.endpoint, JSON.stringify(batch), beacon);
    } catch {
      ok = false;
    }
    // `sendBeacon` devolve falso quando a fila do browser está cheia. Devolver o
    // lote para a fila é o que evita que uma recusa vire buraco silencioso — e no
    // caminho do `beacon` a página já está indo embora, então o que volta serve
    // só para virar `queue.dropped` na próxima sessão. Contar ainda é melhor que
    // sumir.
    if (!ok) this.queue.requeue(events);
  }

  stop(): void {
    if (this.timer !== undefined) clearInterval(this.timer);
    this.timer = undefined;
    this.flush(true);
  }
}

/**
 * Devolve um gravador inerte quando NEVER há sessão de teste — o chamador NEVER
 * ramifica. Mesma forma do `createRecorder` do servidor, e pelo mesmo motivo.
 */
export function createRecorder(deps: RecorderDeps): Recorder {
  const test = deps.session.test;
  if (test === undefined) return inertRecorder;
  const recorder = new ActiveRecorder(deps, test);
  recorder.start();
  return recorder;
}
